import { ColDef } from 'ag-grid-community';
import { FC, useEffect, useState } from 'react';
import { DropdownWrapperMedium } from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import Radio from '../../../components/Radio/Radio';
import Table from '../../../components/Table/Table';
import { useClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import { useMenus, useCompareMenuItemRecipe, useCompareMenuItemSubrecipe } from '../../../services/menu/menu.service';
import { Menu } from '../../../services/menu/menu.types';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import {
  ButtonWrapper,
  ComparePage,
  DropdownButtonWrapper,
  DropdownWrapper,
  ExportButtonWrapper,
  RadioWrapper,
  RowWrapper,
} from './CompareMenus.styles';
import Combobox from '../../../components/Combobox/Combobox';
import rem from '../../../utils/rem/rem';
import { CSVLink } from 'react-csv';
import { Variant } from '../../../components/Button/Button.types';

export const CompareMenus: FC = () => {
  const [menuOneNo, setMenuOneNo] = useState<{ value: number; label: string } | null>(null);
  const [menuTwoNo, setMenuTwoNo] = useState<{ value: number; label: string } | null>(null);
  const [menuOneItemRecipes, setMenuOneItemRecipes] = useState<any>([]);
  const [menuTwoItemRecipes, setMenuTwoItemRecipes] = useState<any>([]);
  const [menuOneItemSubrecipes, setMenuOneItemSubrecipes] = useState<any>([]);
  const [menuTwoItemSubrecipes, setMenuTwoItemSubrecipes] = useState<any>([]);
  const [searchRecipe, setSearchRecipe] = useState<boolean>(false);
  const [searchSubrecipe, setSearchSubrecipe] = useState<boolean>(false);
  const [isRecipes, setIsRecipes] = useState<boolean>(true);
  const { data: classes } = useClasses(true, 0);
  const { data: menus } = useMenus({}, true, 20000);
  const [selectedClass, setSelectedClass] = useState<{ value: number; label: string } | null>(null);

  const {
    data: menuOneItemRecipeData,
    isError: menuOneItemRecipeError,
    remove: removeMenuOneRecipe,
  } = useCompareMenuItemRecipe(
    {
      menuOneNo: menuOneNo?.value,
      menuTwoNo: menuTwoNo?.value,
      classNo: selectedClass?.value,
    },
    searchRecipe
  );

  const {
    data: menuOneItemSubrecipeData,
    isError: menuOneItemSubrecipeError,
    remove: removeMenuOneSubrecipe,
  } = useCompareMenuItemSubrecipe(
    {
      menuOneNo: menuOneNo?.value,
      menuTwoNo: menuTwoNo?.value,
      classNo: selectedClass?.value,
    },
    searchSubrecipe
  );

  useFetchErrorToast([
    { isError: menuOneItemRecipeError, label: 'Error fetching menu items', id: 'menuOneRecipesFetchError' },
    {
      isError: menuOneItemSubrecipeError,
      label: 'Error fetching menu item subrecipes',
      id: 'menuOneSubrecipesFetchError',
    },
  ]);

  const classOptions =
    classes?.data?.map((item: Class) => {
      return { value: item?.classNo, label: `${item?.description} ~ ${item.classNo}` };
    }) || [];

  useEffect(() => {
    if (classes?.data) {
      setSelectedClass(classOptions[0]);
      setSearchRecipe(false);
      setSearchSubrecipe(false);
    }
  }, [classes]);

  useEffect(() => {
    if (isRecipes && menuOneItemRecipeData) {
      setSearchRecipe(false);
      setSearchSubrecipe(false);
      let data = menuOneItemRecipeData.data;
      setMenuOneItemRecipes(data.menuOne);
      setMenuTwoItemRecipes(data.menuTwo);
      removeMenuOneRecipe();
    }
  }, [menuOneItemRecipeData]);

  useEffect(() => {
    if (!isRecipes && menuOneItemSubrecipeData) {
      setSearchRecipe(false);
      setSearchSubrecipe(false);
      let data = menuOneItemSubrecipeData.data;
      setMenuOneItemSubrecipes(data.menuOne);
      setMenuTwoItemSubrecipes(data.menuTwo);
      removeMenuOneSubrecipe();
    }
  }, [menuOneItemSubrecipeData]);

  const menuCompareFn = (itemA: Menu, itemB: Menu) => {
    if (+itemA.storeNo > +itemB.storeNo) {
      return 1;
    }
    if (+itemA.storeNo < +itemB.storeNo) {
      return -1;
    }

    return 0;
  };

  const menuOptions =
    menus?.data?.sort(menuCompareFn).map((item: Menu) => {
      return {
        value: item?.menuNo,
        label: `${item.storeNo} ~ ${item.menuNo} ~ ${item.description} ~ ${item.statusNo}`,
      };
    }) || [];

  const handleRadioChange = () => {
    setIsRecipes((prevState) => !prevState);
    if (isRecipes) {
      setMenuOneItemSubrecipes([]);
      setMenuTwoItemSubrecipes([]);
    } else {
      setMenuOneItemRecipes([]);
      setMenuTwoItemRecipes([]);
    }
    setSelectedClass(classOptions[0]);
    setMenuOneNo(null);
    setMenuTwoNo(null);
  };

  const handleCompareClick = () => {
    setSearchRecipe(false);
    setSearchSubrecipe(false);
    if (isRecipes) {
      setSearchRecipe(true);
    } else {
      setSearchSubrecipe(true);
    }
  };

  const recipesColumnDefs: ColDef[] = [
    { minWidth: 110, field: 'specNo', headerName: 'Spec', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'description', headerName: 'Description', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'strategy', headerName: 'Strategy', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'price', headerName: 'Price', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'classDesc', headerName: 'Class', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'base', headerName: 'Base', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'size', headerName: 'Size', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'product', headerName: 'Product', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'dateRange', headerName: 'Date Range', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'days', headerName: 'Days', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'time', headerName: 'Time', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'occasion', headerName: 'Occasion', floatingFilter: false, flex: 2 },
  ];

  const subrecipesColumnDefs: ColDef[] = [
    { minWidth: 110, field: 'specNo', headerName: 'Spec', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'subSpecNo', headerName: 'Sub Spec', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'description', headerName: 'Description', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'strategy', headerName: 'Strategy', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'price', headerName: 'Price', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'classDesc', headerName: 'Class', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'base', headerName: 'Base', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'size', headerName: 'Size', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'product', headerName: 'Product', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'dateRange', headerName: 'Date Range', floatingFilter: false, flex: 1 },
    { minWidth: 110, field: 'days', headerName: 'Days', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'time', headerName: 'Time', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'occasion', headerName: 'Occasion', floatingFilter: false, flex: 2 },
    { minWidth: 110, field: 'option', headerName: 'Option', floatingFilter: false, flex: 1 },
  ];

  const extractMenuComparisonData = (menuOne: any[], menuTwo: any[], isRecipes: boolean) => {
    const columnDefs = isRecipes ? recipesColumnDefs : subrecipesColumnDefs;

    const validColumnDefs = columnDefs.filter((colDef) => colDef.headerName);

    const headers = ['Menu One No.', 'Menu Two No.', ...validColumnDefs.map((colDef) => `Menu One ${colDef.headerName!}`), ...validColumnDefs.map((colDef) => `Menu Two ${colDef.headerName!}`)];

    const combinedData = menuOne.map((item, index) => {
        const menuTwoItem = menuTwo[index] || {};
        const combinedItem: Record<string, any> = {};

        combinedItem['Menu One No.'] = menuOneNo?.value || '';
        combinedItem['Menu Two No.'] = menuTwoNo?.value || '';

        validColumnDefs.forEach((colDef) => {
            const field = colDef.field;
            if (field) {
                combinedItem[`Menu One ${colDef.headerName!}`] = item[field] || '';
                combinedItem[`Menu Two ${colDef.headerName!}`] = menuTwoItem[field] || '';
            }
        });

        return combinedItem;
    });

    return [headers, ...combinedData];
};


  const [headers, ...csvData] = extractMenuComparisonData(
    isRecipes ? menuOneItemRecipes : menuOneItemSubrecipes,
    isRecipes ? menuTwoItemRecipes : menuTwoItemSubrecipes,
    isRecipes
  );

  return (
    <>
      <Header breadcrumb={{ label: 'Back To All Menus', to: '/menus' }} title='Compare Menus' />
      <ComparePage>
        <H2>Compare Options</H2>
        <RowWrapper>
          <RadioWrapper>
            <Radio label='Recipes' id='recipes' checked={isRecipes} onChange={handleRadioChange} />
          </RadioWrapper>
          <RadioWrapper>
            <Radio
              label='Option Recipes'
              id='optionRecipes'
              checked={!isRecipes}
              onChange={handleRadioChange}
              data-testid='OptionRecipesRadio'
            />
          </RadioWrapper>
          <DropdownWrapperMedium>
            <Combobox
              testId={'ClassCombobox'}
              width={rem(250)}
              value={selectedClass}
              options={classOptions}
              setSelectedOption={(_event, value) => setSelectedClass((_prevState) => value)}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              label={'Class'}
            />
          </DropdownWrapperMedium>
        </RowWrapper>
        <H2>Menus to Compare</H2>
        <DropdownButtonWrapper>
          <DropdownWrapper>
            <Combobox
              testId={'MenuOneCombobox'}
              width={rem(358)}
              value={menuOneNo}
              options={menuOptions.filter((menuOptions) => menuOptions.value !== menuTwoNo?.value)}
              setSelectedOption={(_event, value) => setMenuOneNo(value)}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              label={'Menu 1'}
            />
            <Combobox
              testId={'MenuTwoCombobox'}
              width={rem(358)}
              value={menuTwoNo}
              options={menuOptions.filter((menuOptions) => menuOptions.value !== menuOneNo?.value)}
              setSelectedOption={(_event, value) => setMenuTwoNo(value)}
              isOptionEqualToValue={(option, value) => option.value === value?.value}
              label={'Menu 2'}
            />
            <ButtonWrapper>
              <Button
                label='Compare'
                onClick={handleCompareClick}
                disabled={selectedClass === null || menuOneNo === null || menuTwoNo === null}
                testId={'CompareButton'}
              />
            </ButtonWrapper>
          </DropdownWrapper>
          {/* Out of scope */}
          {/* <Button label='Print' variant={Variant.Secondary} onClick={() => {}} /> */}
        </DropdownButtonWrapper>
        <Table
          showHeader={true}
          headerTitle='Menu 1 Results View'
          rowData={isRecipes ? menuOneItemRecipes : menuOneItemSubrecipes}
          columnDefs={isRecipes ? recipesColumnDefs : subrecipesColumnDefs}
          returnedRecords={isRecipes ? menuOneItemRecipes?.length || 0 : menuOneItemSubrecipes?.length || 0}
          height={'600px'}
          width={'100%'}
        />
        <Table
          showHeader={true}
          headerTitle='Menu 2 Results View'
          rowData={isRecipes ? menuTwoItemRecipes : menuTwoItemSubrecipes}
          columnDefs={isRecipes ? recipesColumnDefs : subrecipesColumnDefs}
          returnedRecords={isRecipes ? menuTwoItemRecipes?.length || 0 : menuTwoItemSubrecipes?.length || 0}
          height={'600px'}
          width={'100%'}
        />
        <ExportButtonWrapper>
            <CSVLink
            data={csvData}
            headers={headers.map((header: any) => ({ label: header, key: header }))}
            filename={`Comparison - Menu-${menuOneNo?.value}-Menu-${menuTwoNo?.value}-${new Date().toISOString().split('T')[0]}.csv`}
            target='_blank'
            className='btn btn-primary'
            style={{ textDecoration: 'none', color: 'inherit', pointerEvents: 
              (isRecipes && (menuOneItemRecipes.length === 0 || menuTwoItemRecipes.length === 0)) ||
              (!isRecipes && (menuOneItemSubrecipes.length === 0 || menuTwoItemSubrecipes.length === 0)) ? 'none' : 'auto' }}
            >
            <Button 
              label='Export to CSV' 
              variant={Variant.Secondary} 
              testId='ExportButton'
              disabled={
              (isRecipes && (menuOneItemRecipes.length === 0 || menuTwoItemRecipes.length === 0)) ||
              (!isRecipes && (menuOneItemSubrecipes.length === 0 || menuTwoItemSubrecipes.length === 0))
              }
            />
            </CSVLink>
        </ExportButtonWrapper>
      </ComparePage>
    </>
  );
};

export default CompareMenus;
